import { useScopePanel } from '~/composables/web-chat/useScopePanel'
import type { ChatScope } from '~~/shared/web-chat/scope'

/**
 * Offer the rung the ROUTE is showing as the chat's scope (AlexAmin/marcus#69).
 *
 * The floating chat bubble is available on every page, so opening it on a case hub gave an unscoped
 * composer prompting "Acotar a un caso" — the case was on screen and the one place it was not used
 * was the control asking for it. Opening a case's own "Nueva conversación" scopes correctly, which
 * is why this reads as a missing default rather than an inconsistent one. A client hub is the same
 * page one rung up, so it offers the client rung on exactly the same terms.
 *
 * **It is an OFFER, and that is the whole design, not caution.** "El ámbito nunca es un efecto
 * secundario de la navegación" (MAR-130/MAR-136, and §6 of the Chat Scope feature doc): the case hub
 * used to scope on enter via a `watch` and unscope on leave via `onBeforeRouteLeave`, and the leave
 * hook could not tell an explicit "Acotar" click from its own default, so it discarded both. Writing
 * the scope here would rebuild exactly that coupling, one component further out — and without the
 * leave hook it is worse, because a chosen rung is permanent until the user drops it (MAR-461):
 * browsing three cases would leave the chat silently filed under the last one looked at, forever.
 *
 * So the three rules this obeys, all inherited:
 *
 * 1. **Never over a scope that is already set.** The offer computes to `null` unless `activeScope` is
 *    global, so the banner can never contradict the row it sits above. Nothing here distinguishes a rung the user picked from one restored out of
 *    `localStorage`, because both are the user's: the remembered one was chosen too, just earlier.
 * 2. **`setScope` is the only writer.** Accepting is a user choosing a rung, so it persists,
 *    remembers and reveals the context rail exactly as the picker does. Nothing writes on navigation.
 * 3. **The names come from the org's own case and client lists**, never from the URL — the same
 *    resolve-or-refuse stance as `useRouteScope` and `useOpenScope`, sharing their one auth-aware
 *    fetch (`ensureScopeLists`, which reads `/api/cases` and `/api/clients`; both apply
 *    `requireOrganization` server-side, so neither list can carry another org's row). An id the org
 *    cannot see resolves to nothing and simply offers nothing.
 *
 * **CASE BEATS CLIENT, and that is decided here rather than left to route matching.** `/app/cases/*`
 * and `/app/clients/*` are disjoint prefixes today, so no path names both — but "which rung wins"
 * must not be a fact about which regex happened to run first. The case leg is tested first because a
 * case is the DEEPER rung, which is the same tie-break `fromWire` applies to a persisted row
 * carrying both ids and `chat.post.ts` applies to a request body carrying both. One rule, three
 * places.
 *
 * The two ids also resolve against SEPARATE lists, so an id naming a case and a client at once
 * resolves by which hub you are standing on, never by which list happened to be searched first.
 *
 * A dismissal is per rung — `kind:id`, not a bare id, or dismissing a client would silence a case
 * that happened to share its uuid — and in memory only: it is about this sitting, not a preference
 * worth persisting.
 */
export function useRouteScopeOffer() {
  const webChat = useWebChatStore()
  const route = useRoute()
  const { cases, clients, ensureScopeLists } = useScopePanel()
  // `useState`, not a module-level ref: a module variable is shared across SSR requests, i.e. one
  // user's dismissals handed to another (the same trap `useScopePanel` documents for its in-flight
  // promise). An array rather than a Set because this is serialised into the payload.
  const dismissed = useState<string[]>('chat-route-scope-dismissed', () => [])

  // The lists are shared and fetched once per session; this is the bounded, one-shot load the house
  // rule asks for in place of a watcher. Everything below is a computed off `route`, `cases` and
  // `clients`, all already reactive, so navigation needs no hook of its own.
  onMounted(() => { void ensureScopeLists() })

  /** The case hub and anything under it. A list page or a non-case route names no id. */
  const routeCaseId = computed(() => /^\/app\/cases\/([^/]+)/.exec(route.path)?.[1] ?? null)
  /** The client hub and anything under it, read the same way. */
  const routeClientId = computed(() => /^\/app\/clients\/([^/]+)/.exec(route.path)?.[1] ?? null)

  /** The rung the route names, before the offer guards — case first, per the note above. */
  const routeRung = computed<ChatScope | null>(() => {
    const caseId = routeCaseId.value
    if (caseId) {
      const found = cases.value.find(c => c.id === caseId)
      if (!found) return null
      return {
        kind: 'case',
        id: found.id,
        name: found.name,
        clientId: found.client_id ?? null,
        clientName: found.client_name ?? null,
      }
    }

    const clientId = routeClientId.value
    if (clientId) {
      const found = clients.value.find(c => c.id === clientId)
      if (!found) return null
      return { kind: 'client', id: found.id, name: found.name }
    }

    return null
  })

  /** What a dismissal is keyed on: the rung, not the bare id. */
  const dismissKey = computed(() => {
    const rung = routeRung.value
    return rung && rung.kind !== 'global' ? `${rung.kind}:${rung.id}` : null
  })

  const routeOffer = computed<ChatScope | null>(() => {
    // Rule 1: an offer over a set scope would contradict the scope row it renders above.
    if (webChat.activeScope.kind !== 'global') return null
    const key = dismissKey.value
    if (!key || dismissed.value.includes(key)) return null
    return routeRung.value
  })

  /** Rule 2 — the user choosing a rung, so it goes through the single writer. */
  async function acceptRouteOffer() {
    const scope = routeOffer.value
    if (!scope) return
    await webChat.setScope(scope)
  }

  function dismissRouteOffer() {
    const key = dismissKey.value
    if (key && !dismissed.value.includes(key)) dismissed.value = [...dismissed.value, key]
  }

  return { routeOffer, acceptRouteOffer, dismissRouteOffer }
}
